import { BillingDetails } from './billings.model';
import { ChoiceSelection } from './choice-options.model';
import { BillingCycle } from './organization.model';
import { Price } from './price.model';
import { Team, TeamRole, TeamType } from './team.model';

export enum Subscription {
  FREE = 'FREE',
  STANDARD = 'STANDARD',
  PROFESSIONAL = 'PROFESSIONAL',
  PREMIUM = 'PREMIUM',
}

export enum UserGroup {
  ADMIN = 'admin',
  BETA_TESTER = 'beta-tester',
}

export enum SubscriptionChangeScope {
  SELECTED_USERS = 'SELECTED_USERS',
  ORGANISATION = 'ORGANISATION',
  INVITATION = 'INVITATION',
}

export enum DowngradeCancelScope {
  SELECTED_USERS = 'SELECTED_USERS',
  ORGANISATION = 'ORGANISATION',
}

export interface UserRegionConfig {
  region: string;
  allSubregions: boolean;
  selectedSubregions?: string[];
  defaultChanged: boolean;
}

export enum UserHistoryAction {
  ORGANISATION_LEAVE = 'organisation-leave',
  REACTIVATION = 'user-reactivation-back-to-organisation',
  UPGRADE_SUGGESTION = 'upgrade-suggestion',
  UPGRADE_SUGGESTION_ACKNOWLEDGED = 'upgrade-suggestion-acknowledged',
}

export interface User {
  email: string;
  firstName: string;
  id: string;
  images?: {
    logo?: string;
    avatar?: string;
  };
  lastName: string;
  countryCode?: string;
  phoneNumber?: string;
  referralCode?: string;
  role: string;
  sector: string;
  organisationType: string;
  blacklist?: string[];
  subscription: Subscription;
  billingDetails?: BillingDetails;
  poNumber?: string;
  groups?: UserGroup[];
  hasSso?: boolean;
  regionConfig: UserRegionConfig;
  notifications?: {
    type: UserHistoryAction;
    message: string;
    data?: {
      subscriptionTier?: Subscription;
    };
  }[];
}

export interface UserWithOrganisation extends User {
  organisation: {
    id: string;
    name: string;
  };
  status: string;
  teams: {
    id: string;
    type: TeamType;
  }[];
  type: string;
}

export interface UserWithOrganisationDetails extends User {
  type: string;
  organisation: {
    id: string;
    name: string;
    teams: {
      id: string;
      role: TeamRole;
    }[];
    joinedAt: string;
    invitedBy: string;
  };
  teams: Team[];
  subscription: Subscription;
  status: string;
  isAccreditedProfessional?: boolean;
  isApplicationAdmin?: boolean;
  isBetaTester?: boolean;
}

export interface UserCredits {
  creditEarned: number;
  creditBalance: number;
  hasParent: boolean;
}

export interface UserUpdateRequest {
  firstName?: string;
  lastName?: string;
  countryCode?: string;
  phoneNumber?: string;
  sector?: string;
  organisationType?: string;
  billingDetails?: BillingDetails;
  poNumber?: string;
  regionConfig?: Omit<UserRegionConfig, 'defaultChanged'>;
}

export interface PasswordChange {
  currentPassword: string;
  newPassword: string;
}

export interface Tier {
  id: Subscription;
  title: string;
  icon: string;
  description: string;
  header: string;
  benefits: string[];
}

export type SubscriptionChangeRequest = {
  scope: SubscriptionChangeScope;
  users?: {
    id: string;
    subscription: Subscription;
  }[];
  subscription?: Subscription;
  organisation?: {
    name?: string;
    logoUrl?: string;
    billingCycle: BillingCycle;
    billingDetails?: BillingDetails;
    billingRecipients?: string[];
  };
  organisationClosure?: {
    centralised: boolean;
    transferTo?: string;
    churnReason?: ChoiceSelection;
  };
};

export type DowngradeCancelRequest = {
  scope: DowngradeCancelScope;
  users?: string[];
};

export interface SubscriptionPrice extends Price {
  billingDate: string;
  subscriptionQuantity: number;
  pendingUserDowngradeCount?: number;
  pendingOrganisationDowngrade?: boolean;
  pendingInvitationsCount?: number;
  referralCredits?: number;
  subscription?: Subscription;
  changes?: {
    integrationApiKeyRemoval?: boolean;
    propertyManagementRemova?: boolean;
  };
}

export interface BillingCyclePrice extends Price {
  billingCycle: BillingCycle;
  executeDate: string;
  billingDate: string;
  subscriptionQuantity: number;
}

export interface UserRegister {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phoneNumber: string;
  countryCode: string;
  sector: string;
  organisationType: string;
  userSignUpIntents: ChoiceSelection[];
  referralCode: string;
  policiesCheckbox: boolean;
  subscription: Subscription;
}

export interface UserRegisterRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phoneNumber?: string;
  countryCode?: string;
  sector: string;
  organisationType: string;
  userSignUpIntents: ChoiceSelection[];
  referralCode?: string;
  policiesCheckbox?: boolean;
  fromMarketingLink?: boolean;
  subscriptionTier?: Subscription;
}

export interface UserConfirmPasswordChange {
  password: string;
  sector?: string;
  countryCode?: string;
  phoneNumber?: string;
}

export interface UserCreate {
  token: string;
  password: string;
  sector: string;
  organisationType: string;
}

export type JWT = {
  expiresIn: number;
  token: string;
};

export interface UserLayersGroup {
  id: string;
  name: string;
  type: string;
  layers?: string[];
}
